import { cardLabel, newDecks, type Card } from '../cards';
import { luckOf, secondChanceFrame } from './luck';
import { FIVE_CARD_NAMES, evaluateFive, type FiveCardHand } from '../poker';
import type { CasinoGame, TableOption } from './types';

/**
 * Jacks or Better video poker. Deal five, tap cards to hold them, tap DRAW
 * to replace the rest. The paytable is the classic "9/6" table except the
 * royal pays 250 (the full 800 only comes with a 5-coin max bet on a real
 * machine), which puts perfect play around 98% — realistic play lower.
 * Each hand is dealt from a fresh single deck.
 */
export const PAYTABLE: Record<FiveCardHand, number> = {
  royalFlush: 250,
  straightFlush: 50,
  fourOfAKind: 25,
  fullHouse: 9,
  flush: 6,
  straight: 4,
  threeOfAKind: 3,
  twoPair: 2,
  jacksOrBetter: 1,
  nothing: 0,
};

/** The ★'s luck: how often a draw that pays nothing is drawn again (the
 *  replaced cards only), by luck level. Calibrated by sim to 103% / 110%;
 *  pinned in luck.test.ts. */
export const SECOND_CHANCE = [0, 0.192, 0.385];

export function videoPokerPay(hand: FiveCardHand): number {
  return PAYTABLE[hand];
}

export interface VideoPokerState {
  phase: 'idle' | 'hold' | 'done';
  hand: Card[];
  held: boolean[];
  deck: Card[];
  bet: number;
  result: FiveCardHand | null;
  returned: number;
  /** A redraw turned a dead hand into a paying one. */
  lucky?: boolean;
}

const PAYTABLE_TEXT = [
  'Royal x250  St.Flush x50',
  '4 Kind x25  Full Hse x9',
  'Flush x6    Straight x4',
  '3 Kind x3   Two Pair x2',
  'Jacks or better x1',
].join('\n');

/** The replaced cards turning over, left to right, to `hand`. */
function drawFrames(state: VideoPokerState, hand: Card[]): string[] {
  const replaced = state.held.map((h, i) => (h ? -1 : i)).filter((i) => i >= 0);
  return replaced.map((_, k) => {
    const shown = new Set(replaced.slice(0, k));
    return `${hand.map((c, i) => (state.held[i] || shown.has(i) ? cardLabel(c) : '??')).join(' ')}\n\nDrawing...`;
  });
}

export const videoPoker: CasinoGame<VideoPokerState> = {
  id: 'videopoker',
  noDealer: true,
  usesLuck: true,
  name: 'Video Poker',
  blurb: 'Jacks or Better. Hold cards, draw once.',
  baseMinBet: 1,
  baseMaxBet: 100,
  specialPerk: 'Luck: a draw that pays nothing may be redrawn.',
  jackpotRule: 'A royal flush.',
  lockedHint: '• hold / draw',
  create: () => ({ phase: 'idle', hand: [], held: [], deck: [], bet: 0, result: null, returned: 0 }),
  locked: (state) => state.phase === 'hold',
  options(state): TableOption[] {
    if (state.phase !== 'hold') {
      return [
        { id: 'deal', label: state.phase === 'done' ? 'Deal again' : 'Deal' },
        { id: 'paytable', label: 'Paytable' },
      ];
    }
    return [
      ...state.hand.map((c, i) => ({ id: `card${i}`, label: `${cardLabel(c)}${state.held[i] ? '  HOLD' : ''}` })),
      { id: 'draw', label: 'DRAW' },
    ];
  },
  describe(state, env, highlighted) {
    if (highlighted === 'paytable') return PAYTABLE_TEXT;
    if (state.phase === 'idle') return 'Deal five cards. Pairs of jacks or better pay.';
    const cards = state.hand.map(cardLabel).join(' ');
    if (state.phase === 'hold') {
      const now = evaluateFive(state.hand);
      return `${cards}\n\nNow: ${FIVE_CARD_NAMES[now]}\n\nTap cards to hold, then DRAW.`;
    }
    const name = FIVE_CARD_NAMES[state.result ?? 'nothing'];
    const net = state.returned - state.bet;
    return `${cards}\n\n${name}\n${state.returned > 0 ? `${state.lucky ? '★ ' : ''}Pays $${state.returned} (${net >= 0 ? '+' : '-'}$${Math.abs(net)})` : `-$${state.bet}`}`;
  },
  act(state, optionId, env) {
    if (state.phase !== 'hold') {
      if (optionId !== 'deal') return { state, bankroll: env.bankroll };
      const deck = env.rng.shuffle(newDecks(1));
      const hand = deck.splice(0, 5);
      return {
        state: { phase: 'hold', hand, held: Array(5).fill(false), deck, bet: env.bet, result: null, returned: 0 },
        bankroll: env.bankroll - env.bet,
      };
    }
    const cardIndex = optionId.startsWith('card') ? Number(optionId.slice(4)) : -1;
    if (cardIndex >= 0) {
      const held = [...state.held];
      held[cardIndex] = !held[cardIndex];
      return { state: { ...state, held }, bankroll: env.bankroll };
    }
    if (optionId !== 'draw') return { state, bankroll: env.bankroll };
    const deck = [...state.deck];
    let hand = state.hand.map((c, i) => (state.held[i] ? c : deck.shift()!));
    let result = evaluateFive(hand);
    // The ★'s luck: a draw that pays nothing is sometimes drawn again.
    let lucky = false;
    let dead: Card[] | null = null;
    if (videoPokerPay(result) === 0 && state.held.some((h) => !h) && env.rng.next() < (SECOND_CHANCE[luckOf(env)] ?? 0)) {
      const lost = hand;
      hand = state.hand.map((c, i) => (state.held[i] ? c : deck.shift()!));
      result = evaluateFive(hand);
      lucky = videoPokerPay(result) > 0;
      if (lucky) dead = lost;
    }
    // The new cards turn over one at a time; a ★ second chance shows the
    // dead hand, then draws again.
    const frames = dead
      ? [...drawFrames(state, dead), secondChanceFrame(`${dead.map(cardLabel).join(' ')}\n\nNothing`), ...drawFrames(state, hand)]
      : drawFrames(state, hand);
    const returned = state.bet * videoPokerPay(result);
    return {
      state: { ...state, phase: 'done', hand, deck, result, returned, lucky },
      bankroll: env.bankroll + returned,
      settled: { wagered: state.bet, returned },
      jackpot: result === 'royalFlush',
      frames,
    };
  },
};
